/*
Pre-auth client branding: the payload comparison used to decide whether the
login screen's `GET api/client` response actually changed anything.

XOL-446. The login screen re-fetches branding on every mount. Writing the
result unconditionally would churn Vuex and localStorage on every visit, so
we compare first and only write on a real difference.

Why not `JSON.stringify(a) === JSON.stringify(b)`:

  * The cached `xolasClient` may be a *full* `GET api/system/client` payload
    left by a previous authenticated session — a superset of the pre-auth
    whitelist (it carries contacts/email/phone). A whole-object compare would
    always report "different" and always write.
  * `latitude` / `longitude` arrive from Eloquent as decimal *strings* whose
    formatting is not stable ('3.1' vs '3.10' vs 3.1).

So compare field-wise over the whitelist the pre-auth endpoint actually
returns (app/Http/Controllers/Auth/ClientController.php in xolas-tms-api),
with numeric fields coerced and null/undefined/'' treated as one value.

No Vue, no axios, no storage — pure functions, so this is directly testable.
Babel 6: no optional chaining, no nullish coalescing, no `.finally()`.
------------------------------------------------------------------------ */

// The pre-auth whitelist, in the order the API declares it.
export const BRANDING_KEYS = [
  'name',
  'shortName',
  'logoPath',
  'colourCode',
  'latitude',
  'longitude',
  'contactsCount'
]

// Fields compared as numbers rather than strings.
const NUMERIC_KEYS = ['latitude', 'longitude', 'contactsCount']

// Fallback map centre used when the client has no coordinates. Matches the
// value main.js has always used for this.
export const DEFAULT_BASE_MAP = { lat: 3.0, lng: 105.0 }

function isAbsent (v) {
  return v === null || v === undefined || v === ''
}

function sameNumber (a, b) {
  // Normalise "no value" first. Number() disagrees with itself here —
  // Number(null) and Number('') are 0 but Number(undefined) is NaN — so
  // without this, null vs undefined would read as a branding change.
  var absentA = isAbsent(a)
  var absentB = isAbsent(b)
  if (absentA || absentB) {
    return absentA && absentB
  }
  var na = Number(a)
  var nb = Number(b)
  // Both present but unparseable ('abc' vs 'xyz') counts as equal — the
  // point is "did the branding change", not "is this a valid number".
  if (isNaN(na) && isNaN(nb)) {
    return true
  }
  return na === nb
}

function sameText (a, b) {
  var sa = (a === null || a === undefined) ? '' : String(a)
  var sb = (b === null || b === undefined) ? '' : String(b)
  return sa === sb
}

/**
 * True when `next` carries different branding than `current`.
 *
 * `current` is whatever is in store.state.xolasClient — possibly {} after a
 * teardown, possibly a full system/client payload from a prior session.
 */
export function brandingDiffers (current, next) {
  if (!next || typeof next !== 'object') {
    // Nothing usable came back; do not overwrite what we have.
    return false
  }
  if (!current || typeof current !== 'object') {
    return true
  }

  for (var i = 0; i < BRANDING_KEYS.length; i++) {
    var key = BRANDING_KEYS[i]
    var isNumeric = NUMERIC_KEYS.indexOf(key) !== -1
    var same = isNumeric
      ? sameNumber(current[key], next[key])
      : sameText(current[key], next[key])
    if (!same) {
      return true
    }
  }
  return false
}

/**
 * Derive the Google Maps centre from a client payload. Mirrors the
 * derivation main.js used inline before XOL-446.
 */
export function brandingToBaseMap (payload) {
  var lat = DEFAULT_BASE_MAP.lat
  var lng = DEFAULT_BASE_MAP.lng
  if (payload) {
    if (payload.latitude) {
      var parsedLat = Number(payload.latitude)
      if (!isNaN(parsedLat)) {
        lat = parsedLat
      }
    }
    if (payload.longitude) {
      var parsedLng = Number(payload.longitude)
      if (!isNaN(parsedLng)) {
        lng = parsedLng
      }
    }
  }
  return { lat: lat, lng: lng }
}

export default {
  BRANDING_KEYS: BRANDING_KEYS,
  DEFAULT_BASE_MAP: DEFAULT_BASE_MAP,
  brandingDiffers: brandingDiffers,
  brandingToBaseMap: brandingToBaseMap
}
